import { useCallback, useEffect, useMemo, useState } from "react";
import { Cake, Plus, Pencil, Trash2, Search, Settings2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiError } from "@/lib/api";
import { formatDate } from "@/components/public/Shared";

const EMPTY_MEMBER = { full_name: "", dob: "", whatsapp: "", family_id: "", ministry: "", status: "active" };

function daysLabel(days) {
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
}

function MemberRow({ m, showAge, onEdit, testId }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={testId}>
      <div className="min-w-0">
        <p className="font-semibold text-navy">{m.full_name}</p>
        <p className="text-xs text-muted-foreground">
          {formatDate(m.dob)}{m.whatsapp ? ` · ${m.whatsapp}` : ""}{m.ministry ? ` · ${m.ministry}` : ""}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {showAge && m.turning_age != null && (
          <span className="rounded-full bg-gold/15 px-2.5 py-0.5 text-xs font-semibold text-gold-dark">Turns {m.turning_age}</span>
        )}
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.status === "inactive" ? "bg-muted text-muted-foreground" : "bg-green-100 text-green-800"}`}>
          {m.status === "inactive" ? "Inactive" : "Active"}
        </span>
        <Button variant="ghost" size="icon" onClick={() => onEdit(m)} aria-label="Edit" data-testid={`${testId}-edit`}>
          <Pencil className="h-4 w-4 text-navy/60" />
        </Button>
      </div>
    </div>
  );
}

export default function Birthdays() {
  const [overview, setOverview] = useState(null);
  const [members, setMembers] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [range, setRange] = useState(7);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [calMonth, setCalMonth] = useState(() => new Date().getMonth());
  const [calYear, setCalYear] = useState(() => new Date().getFullYear());
  const pageSize = 12;

  const loadOverview = useCallback(() => {
    api.get("/admin/birthdays/overview").then((r) => setOverview(r.data)).catch((e) => toast.error(apiError(e, "Could not load birthday overview")));
  }, []);

  const loadMembers = useCallback(() => {
    const params = { page, page_size: pageSize };
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    api.get("/admin/birthdays/members", { params }).then((r) => setMembers(r.data)).catch((e) => toast.error(apiError(e)));
  }, [q, status, page]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => {
    const t = setTimeout(loadMembers, 250);
    return () => clearTimeout(t);
  }, [loadMembers]);

  const reloadAll = () => { loadOverview(); loadMembers(); };

  const settings = overview?.settings || {};
  const upcomingFiltered = useMemo(
    () => (overview?.upcoming || []).filter((u) => u.days_until <= range),
    [overview, range]
  );

  const calendarDays = useMemo(() => {
    const first = new Date(calYear, calMonth, 1);
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first.getDay(); i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    const map = {};
    (overview?.upcoming || []).forEach((u) => {
      const dt = new Date(u.next_birthday + "T00:00:00");
      if (dt.getMonth() === calMonth && dt.getFullYear() === calYear) {
        map[dt.getDate()] = map[dt.getDate()] || [];
        map[dt.getDate()].push(u.full_name);
      }
    });
    return { cells, map };
  }, [overview, calMonth, calYear]);

  const shiftMonth = (delta) => {
    const d = new Date(calYear, calMonth + delta, 1);
    setCalMonth(d.getMonth());
    setCalYear(d.getFullYear());
  };

  const openCreate = () => { setForm(EMPTY_MEMBER); setEditing("new"); };
  const openEdit = (m) => {
    setForm({
      full_name: m.full_name || "", dob: m.dob || "", whatsapp: m.whatsapp || "",
      family_id: m.family_id || "", ministry: m.ministry || "", status: m.status || "active",
    });
    setEditing(m);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/birthdays/members", form);
        toast.success("Birthday record added");
      } else {
        await api.put(`/admin/birthdays/members/${editing.member_id}`, form);
        toast.success("Birthday record updated");
      }
      setEditing(null);
      reloadAll();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/birthdays/members/${deleting.member_id}`);
      toast.success("Record removed");
      setDeleting(null);
      reloadAll();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const totalPages = members ? Math.max(1, Math.ceil(members.total / pageSize)) : 1;

  return (
    <div data-testid="birthdays-page" className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy">Birthdays</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Birthday records use the central member database. WhatsApp numbers are stored privately for a future integration — nothing is sent automatically.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="birthday-settings-btn">
            <Settings2 className="mr-2 h-4 w-4" /> Birthday Settings
          </Button>
          <Button onClick={openCreate} className="bg-navy text-cream hover:bg-navy-light" data-testid="birthday-add-btn">
            <Plus className="mr-2 h-4 w-4" /> Add Member
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-lg border border-gold/50 bg-white p-6 shadow-sm" data-testid="birthdays-today">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-navy">
            <Cake className="h-5 w-5 text-gold" /> Today's Birthdays
          </h2>
          {!overview ? (
            <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
          ) : overview.today.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground" data-testid="birthdays-today-empty">No birthdays today.</p>
          ) : (
            <div className="divide-y divide-border">
              {overview.today.map((m) => (
                <MemberRow key={m.member_id} m={m} showAge={settings.show_age} onEdit={openEdit} testId={`birthday-today-${m.member_id}`} />
              ))}
            </div>
          )}
        </section>

        <section className="rounded-lg border border-border bg-white p-6 shadow-sm" data-testid="birthdays-upcoming">
          <div className="flex items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-navy">
              <CalendarDays className="h-5 w-5 text-gold" /> Upcoming Birthdays
            </h2>
            <div className="flex gap-1 rounded-lg bg-secondary p-1" data-testid="upcoming-range-tabs">
              {[7, 30].map((d) => (
                <button
                  key={d}
                  onClick={() => setRange(d)}
                  data-testid={`upcoming-${d}-days`}
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${range === d ? "bg-navy text-cream" : "text-navy/60 hover:text-navy"}`}
                >
                  {d} days
                </button>
              ))}
            </div>
          </div>
          {upcomingFiltered.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground" data-testid="upcoming-empty">No birthdays in the next {range} days.</p>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {upcomingFiltered.map((u) => (
                <li key={u.member_id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`upcoming-${u.member_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-navy">{u.full_name}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(u.next_birthday)}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${u.days_until === 0 ? "bg-gold/20 text-gold-dark" : "bg-secondary text-navy/70"}`}>
                    {daysLabel(u.days_until)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="rounded-lg border border-border bg-white p-6 shadow-sm" data-testid="birthdays-calendar">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-navy">
            {new Date(calYear, calMonth, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
          </h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => shiftMonth(-1)} data-testid="cal-prev">← Prev</Button>
            <Button variant="outline" size="sm" onClick={() => shiftMonth(1)} data-testid="cal-next">Next →</Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
            <div key={d} className="py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{d}</div>
          ))}
          {calendarDays.cells.map((d, i) => {
            const names = d ? calendarDays.map[d] : null;
            return (
              <div
                key={d ? `${calYear}-${calMonth + 1}-${d}` : `pad-${i}`}
                title={names ? names.join(", ") : ""}
                className={`flex h-9 items-center justify-center rounded-md text-sm ${
                  names ? "bg-gold/20 font-bold text-navy ring-1 ring-gold/50" : d ? "text-foreground/70" : ""
                }`}
                data-testid={names ? `cal-birthday-day-${d}` : undefined}
              >
                {d || ""}
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">Highlighted days have birthdays (next 30 days of records). Hover a day to see names.</p>
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative min-w-64 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name, WhatsApp number, ministry or family ID…" className="pl-9 bg-white" data-testid="birthday-search" />
          </div>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className="w-40 bg-white" data-testid="birthday-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {!members ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : members.items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-white/60 px-8 py-14 text-center" data-testid="birthday-members-empty">
            <Cake className="mx-auto h-8 w-8 text-muted-foreground/50" />
            <p className="mt-3 font-heading text-lg font-bold text-navy">No birthday records found</p>
            <p className="mt-1 text-sm text-muted-foreground">Add a member with their date of birth to get started.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-border bg-white shadow-sm">
              <table className="w-full text-sm" data-testid="birthday-members-table">
                <thead>
                  <tr className="border-b border-border bg-secondary/60 text-left">
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Name</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Date of Birth</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">WhatsApp / Mobile</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Family</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Ministry</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-navy/70">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {members.items.map((m) => (
                    <tr key={m.member_id} className="hover:bg-secondary/30" data-testid={`birthday-row-${m.member_id}`}>
                      <td className="px-4 py-3 font-medium text-navy">{m.full_name}</td>
                      <td className="px-4 py-3 text-foreground/80">{formatDate(m.dob) || "—"}</td>
                      <td className="px-4 py-3 text-foreground/80">{m.whatsapp || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground/70">{m.family_id || "—"}</td>
                      <td className="px-4 py-3 text-foreground/80">{m.ministry || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.status === "inactive" ? "bg-muted text-muted-foreground" : "bg-green-100 text-green-800"}`}>
                          {m.status === "inactive" ? "Inactive" : "Active"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(m)} data-testid={`birthday-edit-${m.member_id}`} aria-label="Edit">
                            <Pencil className="h-4 w-4 text-navy/60" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleting(m)} data-testid={`birthday-delete-${m.member_id}`} aria-label="Delete">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between" data-testid="birthday-pagination">
              <p className="text-xs text-muted-foreground">Page {members.page} of {totalPages} · {members.total} records</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="birthday-prev-page">Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="birthday-next-page">Next</Button>
              </div>
            </div>
          </>
        )}
      </section>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="birthday-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-navy">{editing === "new" ? "Add Birthday Record" : "Edit Birthday Record"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="bd-name">Full Name <span className="text-destructive">*</span></Label>
              <Input id="bd-name" data-testid="birthday-field-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="bd-dob">Date of Birth <span className="text-destructive">*</span></Label>
              <Input id="bd-dob" data-testid="birthday-field-dob" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="bd-whatsapp">WhatsApp / Mobile Number</Label>
              <Input id="bd-whatsapp" data-testid="birthday-field-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="mt-1.5" placeholder="e.g. +91 98765 43210" />
              <p className="mt-1 text-xs text-muted-foreground">Stored privately for a future WhatsApp integration. Never shown publicly.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bd-family">Family ID (optional)</Label>
                <Input id="bd-family" data-testid="birthday-field-family" value={form.family_id} onChange={(e) => setForm({ ...form, family_id: e.target.value })} className="mt-1.5" placeholder="e.g. F001" />
              </div>
              <div>
                <Label htmlFor="bd-ministry">Ministry / Role (optional)</Label>
                <Input id="bd-ministry" data-testid="birthday-field-ministry" value={form.ministry} onChange={(e) => setForm({ ...form, ministry: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="birthday-field-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="birthday-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-navy text-cream hover:bg-navy-light" data-testid="birthday-form-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent data-testid="birthday-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove birthday record?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove “{deleting?.full_name}” from birthday tracking. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="birthday-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="birthday-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SettingsDialog open={settingsOpen} onClose={() => { setSettingsOpen(false); loadOverview(); }} settings={settings} />
    </div>
  );
}

function SettingsDialog({ open, onClose, settings }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        enabled: !!settings.enabled,
        public_enabled: !!settings.public_enabled,
        show_age: !!settings.show_age,
        message: settings.message || "",
        display_duration: settings.display_duration ?? 12,
        style: settings.style || "banner",
        feb29_rule: settings.feb29_rule || "feb28",
      });
    }
  }, [open, settings]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/birthdays/settings", form);
      toast.success("Birthday settings saved");
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  const toggles = [
    ["enabled", "Enable Birthday Feature", "Master switch for birthday detection"],
    ["public_enabled", "Public Homepage Notification", "Show the birthday banner on the public homepage (names only)"],
    ["show_age", "Show Age", "Display the member's turning age (admin view and optional public display)"],
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="birthday-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy">Birthday Settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {toggles.map(([key, label, hint]) => (
            <div key={key} className="flex items-start gap-3">
              <Checkbox id={`bs-${key}`} data-testid={`birthday-setting-${key}`} checked={form[key]} onCheckedChange={(v) => setForm({ ...form, [key]: !!v })} className="mt-0.5" />
              <div>
                <Label htmlFor={`bs-${key}`} className="text-sm font-semibold">{label}</Label>
                <p className="text-xs text-muted-foreground">{hint}</p>
              </div>
            </div>
          ))}
          <div>
            <Label htmlFor="bs-message">Birthday Message</Label>
            <Textarea id="bs-message" data-testid="birthday-setting-message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" />
            <p className="mt-1 text-xs text-muted-foreground">Use [Name] for the member's name and [Age] for their age (age only shows when "Show Age" is on).</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bs-duration">Display Duration (seconds)</Label>
              <Input id="bs-duration" data-testid="birthday-setting-duration" type="number" min="0" max="300" value={form.display_duration} onChange={(e) => setForm({ ...form, display_duration: e.target.value })} className="mt-1.5" />
              <p className="mt-1 text-xs text-muted-foreground">0 = stays until closed</p>
            </div>
            <div>
              <Label>Notification Style</Label>
              <Select value={form.style} onValueChange={(v) => setForm({ ...form, style: v })}>
                <SelectTrigger className="mt-1.5" data-testid="birthday-setting-style"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="banner">Top banner</SelectItem>
                  <SelectItem value="card">Corner card</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>February 29 Birthdays in Non-Leap Years</Label>
            <Select value={form.feb29_rule} onValueChange={(v) => setForm({ ...form, feb29_rule: v })}>
              <SelectTrigger className="mt-1.5" data-testid="birthday-setting-feb29"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="feb28">Celebrate on February 28</SelectItem>
                <SelectItem value="mar1">Celebrate on March 1</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="birthday-settings-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-navy text-cream hover:bg-navy-light" data-testid="birthday-settings-save">
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
